/**
 * Recursive Shadowcasting Visibility (8-Octant)
 *
 * Standard roguelike FOV algorithm. Divides the field of view into 8 octants
 * and scans outward row-by-row, tracking shadow slopes cast by walls.
 *
 * Complexity: O(visible tiles) — only processes tiles that could be visible.
 *
 * Reference: http://www.roguebasin.com/index.php/FOV_using_recursive_shadowcasting
 */

import type { GridMap, VisibilityResult } from './types';
import { inBounds, blocksLight, coordKey } from './grid';

// ============================================================================
// OCTANT TRANSFORMS
// ============================================================================

/**
 * Each octant maps (row, col) relative to origin into (dx, dy) world offsets.
 * The 8 transforms cover all directions:
 *   octant 0: ESE    octant 1: ENE
 *   octant 2: NNE    octant 3: NNW
 *   octant 4: WNW    octant 5: WSW
 *   octant 6: SSW    octant 7: SSE
 */
function transformOctant(octant: number, row: number, col: number): { dx: number; dy: number } {
  switch (octant) {
    case 0: return { dx: col, dy: -row };
    case 1: return { dx: row, dy: -col };
    case 2: return { dx: row, dy: col };
    case 3: return { dx: col, dy: row };
    case 4: return { dx: -col, dy: row };
    case 5: return { dx: -row, dy: col };
    case 6: return { dx: -row, dy: -col };
    case 7: return { dx: -col, dy: -row };
    default: return { dx: 0, dy: 0 };
  }
}

// ============================================================================
// LIGHT FALLOFF
// ============================================================================

function lightLevel(dist: number, radius: number): number {
  if (dist <= 1) return 1.0;
  const t = dist / radius;
  return Math.max(0, 1.0 - t * t);
}

function dist(dx: number, dy: number): number {
  return Math.sqrt(dx * dx + dy * dy);
}

// ============================================================================
// SHADOWCASTING
// ============================================================================

function scanOctant(
  map: GridMap,
  ox: number,
  oy: number,
  radius: number,
  octant: number,
  row: number,
  startSlope: number,
  endSlope: number,
  result: VisibilityResult,
  openDoors?: Set<string>,
): void {
  if (startSlope <= endSlope) return;
  if (row > radius) return;

  let nextStartSlope = startSlope;

  for (let currentRow = row; currentRow <= radius; currentRow++) {
    if (nextStartSlope <= endSlope) break; // slope range exhausted

    let blocked = false;

    // Clamp nextStartSlope if the wall tile just above the scan range
    // casts a shadow into it. Without this, walls skipped by maxCol
    // rounding leave nextStartSlope stale, leaking visibility at
    // higher rows where rounding re-includes the column.
    const peekCol = Math.floor(currentRow * nextStartSlope) + 1;
    const { dx: pdx, dy: pdy } = transformOctant(octant, currentRow, peekCol);
    const pmx = ox + pdx;
    const pmy = oy + pdy;
    const peekIsOutside = !inBounds(map, pmx, pmy);
    const peekOpaque = peekIsOutside
      || dist(pdx, pdy) > radius
      || blocksLight(map, pmx, pmy, openDoors);
    if (peekOpaque) {
      const peekRight = (peekCol - 0.5) / (currentRow + 0.5);
      if (peekRight < nextStartSlope) {
        nextStartSlope = peekRight;
        if (nextStartSlope <= endSlope) break;
      }
    }

    const minCol = Math.ceil(currentRow * endSlope);
    const maxCol = Math.floor(currentRow * nextStartSlope);

    for (let col = maxCol; col >= minCol; col--) {
      const { dx, dy } = transformOctant(octant, currentRow, col);
      const mx = ox + dx;
      const my = oy + dy;

      const leftSlope = (col + 0.5) / (currentRow - 0.5);
      const rightSlope = (col - 0.5) / (currentRow + 0.5);

      // Determine opacity: OOB and beyond-radius tiles block light
      const isOutside = !inBounds(map, mx, my);
      const d = isOutside ? radius + 1 : dist(dx, dy);
      const isBeyondRadius = d > radius;
      const isOpaque = isOutside || isBeyondRadius
        || blocksLight(map, mx, my, openDoors);

      // Only mark visible if in-bounds AND within radius
      if (!isOutside && !isBeyondRadius) {
        const key = coordKey(mx, my);
        result.visibleSet.add(key);
        const existing = result.lightValues.get(key) ?? 0;
        const newLight = lightLevel(d, radius);
        if (newLight > existing) {
          result.lightValues.set(key, newLight);
        }
      }

      // Shadow tracking runs for ALL tiles (walls, OOB, beyond-radius)
      if (isOpaque) {
        if (!blocked) {
          scanOctant(map, ox, oy, radius, octant, currentRow + 1,
            nextStartSlope, leftSlope, result, openDoors);
        }
        blocked = true;
        nextStartSlope = rightSlope;
      } else {
        blocked = false;
      }
    }

    if (blocked) break;
  }
}

// ============================================================================
// PUBLIC API
// ============================================================================

/** Default vision radius in tiles */
export const DEFAULT_VISION_RADIUS = 12;

/**
 * Compute visible tiles from (originX, originY) with given radius.
 * Returns the set of visible coordinate keys and their light values.
 */
export function computeVisibility(
  map: GridMap,
  originX: number,
  originY: number,
  radius: number = DEFAULT_VISION_RADIUS,
  openDoors?: Set<string>,
): VisibilityResult {
  const result: VisibilityResult = {
    visibleSet: new Set<string>(),
    lightValues: new Map<string, number>(),
  };

  // Origin is always visible
  const originKey = coordKey(originX, originY);
  result.visibleSet.add(originKey);
  result.lightValues.set(originKey, 1.0);

  // Scan all 8 octants
  for (let octant = 0; octant < 8; octant++) {
    scanOctant(map, originX, originY, radius, octant, 1, 1.0, 0.0, result, openDoors);
  }

  return result;
}
